import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowUpRight, QrCode, ShieldCheck, MoveRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { HeroCarousel } from "@/components/HeroCarousel";
import { SectionHead } from "@/components/SectionHead";
import { ProductCard } from "@/components/ProductCard";
import { TrustBar } from "@/components/TrustBar";
import { Testimonials } from "@/components/Testimonials";
import { Reveal, Stagger, StaggerItem, Marquee } from "@/lib/motion";
import { VALUES, JOURNEY, MARQUEE_ITEMS } from "@/data/content";
import { IMG, SEAL, EMBLEM_TRUST, EMBLEM_FAMILY } from "@/data/images";
import { imgSrc } from "@/lib/img";
import { useSettings } from "@/context/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const [products, setProducts] = useState([]);
  const s = useSettings();
  useEffect(() => {
    axios.get(`${API}/products`).then(({ data }) => setProducts(data.slice(0, 4))).catch(console.error);
  }, []);

  return (
    <div data-testid="home-page">
      <HeroCarousel />

      <Marquee items={MARQUEE_ITEMS} />

      <section className="py-20 sm:py-28 bg-white border-y border-forest/10">
        <div className="container-nb">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
            <SectionHead center={false} className="mb-0" eyebrow="All Products" title="Pure. Natural. Thoughtfully crafted." />
            <Reveal delay={0.15}>
              <Link to="/collection" data-testid="explore-all-products-button" className="group inline-flex items-center gap-2.5 bg-forest text-cream text-xs font-semibold uppercase tracking-[0.18em] px-7 py-3.5 rounded-full hover:bg-gold transition-colors duration-300">
                Explore All Products <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </Stagger>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="container-nb">
          <SectionHead eyebrow="Our Promise" title="We don't sell products. We earn trust." />
          <Stagger className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { img: EMBLEM_TRUST, alt: "Nutribest Trust Seal", text: "If it's from Nutribest, I don't need to worry about quality." },
              { img: EMBLEM_FAMILY, alt: "Nutribest Family Promise", text: "If it isn't good enough for our own family, it isn't good enough for our customers." },
            ].map(({ img, alt, text }) => (
              <StaggerItem key={text}>
                <div className="flex items-center gap-5 bg-white rounded-2xl border border-forest/10 p-7 hover:border-gold/40 hover:shadow-lg hover:shadow-forest/5 transition-all duration-500">
                  <img src={img} alt={alt} className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 object-contain" />
                  <p className="font-serif text-lg text-forest leading-snug">{text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-creamdark/40 border-y border-forest/10">
        <div className="container-nb">
          <SectionHead eyebrow="What We Stand For" title="Six standards. Zero compromise." />
          <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-forest/10 rounded-2xl overflow-hidden border border-forest/10">
            {VALUES.map(({ icon: Icon, title, text }) => (
              <StaggerItem key={title} className="bg-cream">
                <div data-testid={`value-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="h-full p-6 text-center group hover:bg-white transition-colors duration-500">
                  <span className="mx-auto w-12 h-12 rounded-full border border-forest/15 flex items-center justify-center text-forest group-hover:border-gold group-hover:text-gold group-hover:scale-110 transition-all duration-500">
                    <Icon size={20} strokeWidth={1.5} />
                  </span>
                  <h3 className="mt-4 text-[11px] font-bold uppercase tracking-[0.16em] text-forest">{title}</h3>
                  <p className="mt-2 text-xs text-mutedgreen leading-relaxed">{text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="container-nb">
          <SectionHead eyebrow="From Farm to Family" title="A journey of care, in seven steps." />
          <Stagger className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-6">
            {JOURNEY.map(({ icon: Icon, title, text }, i) => (
              <StaggerItem key={title}>
                <div data-testid={`journey-step-${i + 1}`} className="text-center group">
                  <div className="relative mx-auto w-16 h-16 rounded-full border border-gold/50 bg-white flex items-center justify-center text-forest group-hover:bg-forest group-hover:text-cream transition-colors duration-500">
                    <Icon size={22} strokeWidth={1.5} />
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gold text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                  </div>
                  <h4 className="mt-4 text-[11px] font-bold uppercase tracking-[0.16em] text-forest">{title}</h4>
                  <p className="mt-2 text-[11px] text-mutedgreen leading-relaxed">{text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal className="text-center mt-12">
            <Link to="/farm-to-family" data-testid="journey-discover-link" className="link-underline inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-forest">
              Discover the full journey <MoveRight size={14} className="text-gold" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Testimonials />

      <section className="relative overflow-hidden bg-forestdeep grain">
        <div className="container-nb py-24 sm:py-32 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="eyebrow !text-goldlight mb-5">Our Quality Promise</p>
            <h2 className="font-serif text-3xl sm:text-5xl text-cream leading-[1.15]">Every product carries our name. Every product carries our promise.</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="bg-cream/5 border border-cream/15 rounded-2xl p-8 backdrop-blur-sm">
              <span className="w-12 h-12 rounded-full border border-goldlight/40 flex items-center justify-center text-goldlight"><ShieldCheck size={20} strokeWidth={1.5} /></span>
              <p className="mt-5 font-serif text-xl text-cream leading-snug">"If it isn't good enough for our own family, it isn't good enough for our customers."</p>
              <Link to="/quality-standard" data-testid="quality-learn-more-link" className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-goldlight hover:text-cream transition-colors">
                Learn More <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="container-nb grid lg:grid-cols-[auto_1fr_auto] gap-12 items-center">
          <Reveal className="mx-auto">
            <img src={SEAL} alt="The Nutribest Trust Seal" data-testid="trust-seal-image" className="w-40 h-40 sm:w-48 sm:h-48 object-cover rounded-full mix-blend-multiply animate-float-soft" />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="eyebrow mb-3">The Nutribest Trust Standard™</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-forest leading-snug">Every product meets our six uncompromising standards.</h2>
            <div className="mt-6 flex flex-wrap gap-x-7 gap-y-3">
              {["Premium Ingredients", "Responsible Sourcing", "Hygienic Processing", "Rigorous Testing", "Safe & Sustainable Packaging", "Customer First"].map((s) => (
                <span key={s} className="flex items-center gap-2 text-xs text-mutedgreen font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold" /> {s}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2} className="mx-auto text-center lg:text-left">
            <div className="inline-flex flex-col items-center gap-3 border border-dashed border-gold/50 rounded-2xl p-6">
              <QrCode size={56} strokeWidth={1.2} className="text-forest" />
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-forest">Scan to see<br />our promise & journey</p>
              <p className="text-[10px] text-mutedgreen">Traceability. Transparency. Trust.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <TrustBar quote={"\u201CIf it\u2019s from Nutribest, I don\u2019t need to worry about quality.\u201D"} />
    </div>
  );
}
